import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_PAYROLL_INSPECTION } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { ProjectDependsOn } from "@/schemas/commonFields";
import { PAYROLL_STATUS_CREATED } from "@/schemas/payroll";
import { PayrollsService } from "@/services/payrolls";
import { CirclePlus } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { PayrollConfirmButton } from "./PayrollConfirmButton";

interface PayrollCreateButtonProps {
  projectDependsOn: ProjectDependsOn | undefined;
}

export const PayrollCreateButton = memo(
  ({ projectDependsOn }: PayrollCreateButtonProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const { payrollId } = usePayrollParams();
    const { toast } = useToast();
    const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
      Module.Payrolls,
    );

    const mutationFn = async (payrollId: string) => {
      if (projectDependsOn === undefined) {
        return;
      }
      await PayrollsService.createPayroll(payrollId, projectDependsOn);
    };

    const mutation = useMutationWithErrorHandling({
      errorTitle: t("payrolls.errors.create"),
      mutationFn,
      onSuccess: () => {
        toast({
          title: t("payrolls.success.create"),
        });
        updateQueryDataForExistingItem({
          id: payrollId,
          payroll_status: PAYROLL_STATUS_CREATED,
        });
        navigate(
          generatePath(PATH_PAYROLL_INSPECTION, {
            payrollId,
          }),
        );
      },
    });

    const onConfirm = useCallback(() => {
      mutation.mutateAsync(payrollId);
    }, [mutation, payrollId]);

    return (
      <PayrollConfirmButton
        icon={CirclePlus}
        translationKey="payrolls.createPayroll"
        open={open || mutation.isPending}
        onOpenChange={setOpen}
        onConfirm={onConfirm}
        isPending={mutation.isPending}
        disabled={projectDependsOn === undefined}
      />
    );
  },
);
